import React, { useContext, useEffect, useState } from "react";
import {
  CART_STEP,
  PAYMENT_METHOD,
  CartContext,
} from "../../contexts/CartContext";
import AppContext from "../../AppContext";
import { langComponent } from "../../constants";
import CustomCheckbox from "../WorkExchange/CustomCheckbox";
import Button from "../common/Button";
import { formatNumber } from "../../helpers/common.helper";

const assetsUri = import.meta.env.ASSETS_URI;

export const Summary = (props) => {
  const { getLang, designStyles } = useContext(AppContext);
  const {
    items,
    selections,
    step,
    checkoutCart,
    paymentMethod,
    setPaymentMethod,
    cardDetails,
    chargeJournal,
    checkingOut,
    charging,
  } = useContext(CartContext);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationShoppingCart = getLang(langComponent.chat);
  const TranslationWorkExchange = getLang(langComponent.workExchange);
  const [includeVat, setIncludeVat] = useState(false);

  const [detail, setDetail] = useState({
    subTotal: 0,
    vat: 0,
    charge: 0,
    discounts: 0,
    grandTotal: 0,
  });

  useEffect(() => {
    let selectedItems = items;

    if (selections.length > 0) {
      selectedItems = selections.map((s) => {
        return items.find((item) => item._id === s);
      });
    }

    let subTotal = selectedItems?.reduce((previous, current) => {
      return previous + current.qty * current.data.price;
    }, 0);

    let discounts = selectedItems?.reduce((previous, current) => {
      const discount = current.data.discount ?? 0;
      return previous + discount;
    }, 0);

    const netTotal = subTotal - discounts;

    const vat = includeVat ? (netTotal * 7) / 100 : 0;

    let charge = 0;

    if (paymentMethod === PAYMENT_METHOD.CARD) {
      charge = (netTotal * 3.65) / 100;
    }

    if (paymentMethod === PAYMENT_METHOD.TRUEMONEY) {
      charge = (netTotal * 2.65) / 100;
    }

    if (paymentMethod === PAYMENT_METHOD.QR) {
      charge = (netTotal * 1.65) / 100;
    }

    const grandTotal = subTotal - discounts + vat + charge;

    setDetail({
      subTotal,
      discounts,
      vat,
      charge,
      grandTotal,
    });
  }, [selections, includeVat, paymentMethod]);

  const handleCheckoutClick = () => {
    checkoutCart(includeVat);
  };

  const handleSubmitCreditCard = async () => {
    await chargeJournal(PAYMENT_METHOD.CARD);
  };

  const handleCheckIncludeVat = (checked) => {
    setIncludeVat(checked);
  };

  const { subTotal, vat, charge, discounts, grandTotal } = detail;

  return (
    <div className="cart-content-summary">
      <div className="cart-content-summary-title">
        {TranslationWorkExchange("Order summary")}
        {selections.length > 0 && (
          <div className="cart-content-summary-title-total">
            {selections.length} {TranslationWorkExchange("Item")}
          </div>
        )}
      </div>
      <div className="cart-content-summary-detail">
        {step !== CART_STEP.INFO && (
          <div className="cart-content-summary-detail-items">
            {(selections.length > 0
              ? items.filter((i) => selections.includes(i._id))
              : items
            ).map((selection) => {
              const {
                _id,
                data: {
                  banner,
                  name,
                  price,
                  discount,
                  designType: { workExchange },
                  active,
                  deleted,
                },
              } = selection;

              const category = designStyles.workExchanges.find(
                (we) => we.value === workExchange
              )?.name;

              let priceStyling = {};

              if (discount) {
                priceStyling = {
                  textDecoration: "line-through",
                  fontSize: 10,
                  fontWeight: 400,
                  color: "#bebebe",
                };
              }

              // * Check if item is unavailable
              const unavailable = !active || deleted;
              const errorText = unavailable ? "Unavailable" : "";

              const renderPrice = () => {
                return (
                  <div className="cart-content-summary-detail-items-card__price">
                    <span
                      style={{
                        fontSize: 14,
                        ...priceStyling,
                      }}
                    >
                      {price
                        ? formatNumber(price, !discount ? "฿" : "")
                        : "Free"}
                    </span>
                    {!!discount && (
                      <span style={{ fontSize: 14 }}>
                        {formatNumber(price - discount, "฿")}
                      </span>
                    )}
                  </div>
                );
              };

              return (
                <div
                  className={`cart-content-summary-detail-items-card`}
                  key={_id}
                >
                  <img
                    className="cart-content-summary-detail-items-card__banner"
                    alt="banner"
                    src={`${assetsUri}/${banner}`}
                    onError={({ target }) => {
                      target.src = banner;
                    }}
                  />
                  <div className="cart-content-summary-detail-items-card-info">
                    <span className="cart-content-summary-detail-items-card__name">
                      {name}
                    </span>
                    {category && (
                      <span className="cart-content-summary-detail-items-card__category">
                        {category}
                      </span>
                    )}
                    {renderPrice()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="cart-content-summary-detail-receipt">
          <div className="cart-content-summary-detail-receipt__row">
            <div>{TranslationWorkExchange("Subtotal")}</div>
            <div>
              ฿
              {(subTotal ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="cart-content-summary-detail-receipt__row cart-content-summary-detail-receipt__row--small">
            <div>{TranslationWorkExchange("Discount")}</div>
            <div>
              -฿
              {(discounts ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div className="cart-content-summary-detail-receipt__row cart-content-summary-detail-receipt__row--small">
            <div>
              {TranslationWorkExchange("Charge")}{" "}
              {paymentMethod == PAYMENT_METHOD.TRUEMONEY
                ? "(2.65%)"
                : paymentMethod == PAYMENT_METHOD.CARD
                ? "(3.65%)"
                : paymentMethod == PAYMENT_METHOD.QR
                ? "(1.65%)"
                : ""}
            </div>
            <div>
              {" +฿"}
              {(charge ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          {(step === CART_STEP.INFO ||
            (step !== CART_STEP.INFO && includeVat)) && (
            <div
              className="cart-content-summary-detail-receipt__row cart-content-summary-detail-receipt__row--small"
              style={{ marginLeft: step === CART_STEP.INFO ? -4 : 0 }}
            >
              {step === CART_STEP.INFO && (
                <CustomCheckbox
                  onChange={() => handleCheckIncludeVat(!includeVat)}
                  checked={includeVat}
                />
              )}
              <div>{TranslationShoppingCart("Vat (7%)")}</div>
              <div>
                +฿
                {(vat ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          )}
          <div className="cart-content-summary-detail-receipt__separator" />
          <div className="cart-content-summary-detail-receipt__row">
            <div>{TranslationShoppingCart("Grand Total")}</div>
            <div style={{ fontWeight: "500" }}>
              ฿
              {(grandTotal ?? 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>
        {items.length > 0 && (
          <>
            {step === CART_STEP.INFO && (
              <div className="cart-content-summary-detail-footer">
                <Button
                  onClick={handleCheckoutClick}
                  label={TranslationWorkExchange("Check out")}
                  submitting={checkingOut}
                />
              </div>
            )}
            {step === CART_STEP.PAYMENT &&
              paymentMethod === PAYMENT_METHOD.CARD && (
                <div className="cart-content-summary-detail-footer">
                  <Button
                    onClick={handleSubmitCreditCard}
                    label={
                      charging
                        ? TranslationShoppingCart("Charging")
                        : TranslationShoppingCart("Pay")
                    }
                    disabled={!cardDetails}
                    submitting={charging}
                  />
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};
